import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

interface CircularProgressProps {
  percentage: number;
  label: string;
}

const CircularProgress: React.FC<CircularProgressProps> = ({ percentage, label }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  // Bigger radius and stroke
  const radius = 100; // was 70
  const strokeWidth = 20; // was 15
  const circumference = 2 * Math.PI * radius;

  const size = 240; // overall SVG size (was 160)
  const center = size / 2;

  // Calculate target offset
  const targetOffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative flex flex-col items-center justify-center p-6">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          className="text-muted-foreground opacity-30"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          r={radius}
          cx={center}
          cy={center}
        />
        {/* Animated progress circle */}
        <motion.circle
          ref={ref}
          className="text-primary"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          r={radius}
          cx={center}
          cy={center}
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={
            isInView
              ? { strokeDashoffset: targetOffset }
              : { strokeDashoffset: circumference }
          }
          transition={{ duration: 1.5, ease: 'easeOut' }}
        />
      </svg>

      {/* Overlay content */}
      <div className="absolute flex flex-col items-center justify-center">
        <span className="text-5xl font-extrabold text-foreground">{percentage}%</span>
        <p className="mt-3 text-center text-base font-medium text-muted-foreground max-w-[180px]">
          {label}
        </p>
      </div>
    </div>
  );
};

export default CircularProgress;
